import React, { useEffect, useState } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import ErrorMessage from '../../components/errormessage';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { useStores } from '../../hooks/use-stores';
import FormDef from '../../forms/template';
import ACLEditor from './ACLEditor';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import SimpleTable from '../../components/super/SimpleTable';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '../../components/input/textField';
import MUITextField from '@material-ui/core/TextField';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@material-ui/core';
import AssessmentTemplateQuestions from './AssessmentTemplateQuestions';

function AssessmentTemplate() {
    const { superStore, commonStore } = useStores();
    let { templateId } = useParams();
    const navigate = useNavigate();
    const isNew = templateId === 'create';
    const {
        loadingAssessmentTemplate,
        editingAssessmentTemplate,
        assessmentTemplatesError,
        editingAssessmentTemplateQuestions,
        updatingAssessmentTemplate,
        creatingAssessmentTemplate,
        deletingAssessmentTemplate,
    } = superStore;

    const [edit, setEdit] = useState(false);
    const [questionsDialogOpen, setQuestionsDialogOpen] = useState(false);

    console.log("templateId", templateId, "isNew", isNew);

    const loadData = async () => {
        if (!isNew) {
            await superStore.loadAssessmentTemplate(templateId);
            await superStore.loadAssessmentTemplateQuestions(templateId);
        } else {
            setEdit(true);
        }
    };

    const localStore = useLocalObservable(() => {
        let _form = new FormDef({
            async onSuccess(form) {
                if (isNew) {
                    const result = await superStore.createAssessmentTemplate(
                        form.values()
                    );
                    setEdit(false);
                    navigate('/content/assessment-templates/' + result.id);
                } else {
                    const result = await superStore.saveAssessmentTemplate(
                        templateId,
                        form.values()
                    );
                    if (result) {
                        setEdit(false);
                    }
                }
            },
            onError() {
                console.log('Error');
            },
        });

        return { _form };
    });

    useEffect(() => {
        loadData();
    }, [templateId]);

    const handleEdit = () => {
        localStore._form.update(editingAssessmentTemplate);
        setEdit(true);
    };

    const handleDelete = () => {
        commonStore.showConfirm(
            `Delete assessment template "${editingAssessmentTemplate?.title}"?`,
            'Delete',
            'Delete assessment template',
            async () => {
                await superStore.deleteAssessmentTemplate(templateId);
                navigate('/content/assessment-templates');
            }
        );
    };

    const handleSave = (e) => {
        localStore._form.onSubmit(e);
    };

    const handleOpenQuestionsDialog = async () => {
        if (templateId && !isNew) {
            // Load questions list and refresh template questions when dialog opens
            await superStore.loadQuestions();
            await superStore.loadAssessmentTemplateQuestions(templateId);
        }
        setQuestionsDialogOpen(true);
    };

    const handleCloseQuestionsDialog = () => {
        setQuestionsDialogOpen(false);
    };

    const columns = React.useMemo(
        () => [
            {
                Header: 'Id',
                accessor: 'question_id',
                Cell: (x) => (
                    <Link to={`/content/questions/${x.value}/edit`}>
                        {x.value}
                    </Link>
                ),
            },
            {
                Header: 'Name',
                accessor: 'name',
                Cell: (x) => (
                    <Link to={`/content/questions/${x.row.original.question_id}/edit`}>
                        {x.value}
                    </Link>
                ),
            },
            {
                Header: 'Code',
                accessor: 'external_id',
            },
        ],
        []
    );

    return loadingAssessmentTemplate || (!editingAssessmentTemplate && !isNew) ? (
        <ThemedSpinner />
    ) : (
        (editingAssessmentTemplate || isNew) && (
            <Grid
                container
                direction="row"
                justify="center"
                alignItems="center"
                align="center"
                spacing={2}
            >
                {edit ? (
                    <>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                Component={MUITextField}
                                field={localStore._form.$('title')}
                            ></TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                Component={MUITextField}
                                field={localStore._form.$('tip')}
                            ></TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                Component={MUITextField}
                                field={localStore._form.$('description')}
                            ></TextField>
                            <TextField
                                fullWidth
                                variant="outlined"
                                Component={MUITextField}
                                field={localStore._form.$('frequency')}
                            ></TextField>
                        </Grid>
                        <Grid item xs={12}>
                            {assessmentTemplatesError && (
                                <ErrorMessage
                                    error={assessmentTemplatesError}
                                    errorType={'error'}
                                ></ErrorMessage>
                            )}
                            {(updatingAssessmentTemplate ||
                                creatingAssessmentTemplate) && (
                                <CircularProgress />
                            )}
                            <Button
                                variant={'contained'}
                                color={'primary'}
                                onClick={handleSave}
                                disabled={
                                    updatingAssessmentTemplate ||
                                    creatingAssessmentTemplate
                                }
                            >
                                Save
                            </Button>
                            <Button
                                variant={'contained'}
                                color={'secondary'}
                                disabled={
                                    updatingAssessmentTemplate ||
                                    creatingAssessmentTemplate
                                }
                                onClick={() => {
                                    if (isNew) {
                                        navigate('/content/assessment-templates');
                                    } else {
                                        setEdit(false);
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid item xs={12}>
                            <Box mt={4}>
                                <Typography variant="h2" gutterBottom>
                                    Title: {editingAssessmentTemplate?.title}
                                </Typography>
                                <Button
                                    variant={'contained'}
                                    color={'primary'}
                                    onClick={handleEdit}
                                >
                                    Edit
                                </Button>{' '}
                                <Button
                                    variant={'contained'}
                                    color={'secondary'}
                                    onClick={handleDelete}
                                    disabled={deletingAssessmentTemplate?.length > 0}
                                    startIcon={<DeleteIcon />}
                                >
                                    Delete
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body1" gutterBottom>
                                <strong>Description:</strong>{' '}
                                {editingAssessmentTemplate?.description}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body1" gutterBottom>
                                <strong>Tip:</strong>{' '}
                                {editingAssessmentTemplate?.tip}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body1" gutterBottom>
                                <strong>Frequency:</strong>{' '}
                                {editingAssessmentTemplate?.frequency}
                            </Typography>
                        </Grid>
                    </>
                )}

                {!isNew && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="h4">Questions</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleOpenQuestionsDialog}
                                startIcon={<EditIcon />}
                            >
                                Edit Questions
                            </Button>
                        </Grid>
                        {editingAssessmentTemplateQuestions &&
                        editingAssessmentTemplateQuestions.length > 0 ? (
                            <Grid item xs={8}>
                                <SimpleTable
                                    columns={columns}
                                    data={editingAssessmentTemplateQuestions}
                                    needsFilter={
                                        editingAssessmentTemplateQuestions.length >
                                        5
                                    }
                                />
                            </Grid>
                        ) : (
                            <Grid item xs={8}>
                                <Typography>No questions found :(</Typography>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Box mt={4}>
                                <Typography variant="h4" gutterBottom>
                                    Access control
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            {editingAssessmentTemplate.acl && editingAssessmentTemplate.unique_id && (
                                <ACLEditor
                                    acl={editingAssessmentTemplate.acl}
                                    unique_id={editingAssessmentTemplate.unique_id}
                                />
                            )}
                        </Grid>
                    </>
                )}

                {assessmentTemplatesError && (
                    <Grid item xs={12}>
                        <ErrorMessage error={assessmentTemplatesError} />
                    </Grid>
                )}

                {!isNew && (
                    <Dialog
                        open={questionsDialogOpen}
                        onClose={handleCloseQuestionsDialog}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>Edit Questions</DialogTitle>
                        <DialogContent>
                            <AssessmentTemplateQuestions templateId={templateId} />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={handleCloseQuestionsDialog}
                                color="primary"
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            </Grid>
        )
    );
}

export default observer(AssessmentTemplate);

